import {defineStore} from "pinia";
import api from "@/api.js";
import router from "@/router/index.js";
import {getAuthenticatedRequest} from "@/stores/AuthenticatedRequest.js";
import {useUserStore} from '@/stores/user.js';
import {useApi} from "@/services/useApi.js";

function summarizeOrders(orders, fieldMap = {}) {
  const summary = {
    records: orders.length
  }

  // مقداردهی اولیه
  Object.keys(fieldMap).forEach(key => {
    summary[key] = 0
  })

  // جمع مقادیر
  orders.forEach(order => {
    Object.entries(fieldMap).forEach(([summaryKey, orderField]) => {
      summary[summaryKey] += parseFloat(order[orderField] || 0)
    })
  })

  // رُند کردن اعشاری
  Object.keys(fieldMap).forEach(key => {
    summary[key] = parseFloat(summary[key].toFixed(2))
  })

  return summary
}

const ORDER_TYPES = {
  Account: {
    subRequest: 10035,
    key: 'accountOrder',
    targetKey: 'accountOrders',
    filterKey: 'accountOrder',
    responseKey: 'accountOrders',
    changesKey: 'accountOrder',
    addSubRequest: 10037,
    updateSubRequest: 10038,
    removeSubRequest: 10039,
    detailsSubRequest: 10036,
    summaryFields: {sold: 'price', income: 'income', cut: 'takersCut', revenue: 'revenue'},
  },
  Boost: {
    subRequest: 10040,
    key: 'boostOrder',
    targetKey: 'boostOrders',
    filterKey: 'boostOrder',
    responseKey: 'boostOrders',
    changesKey: 'boostOrder',
    addSubRequest: 10042,
    updateSubRequest: 10043,
    removeSubRequest: 10044,
    detailsSubRequest: 10041,
    summaryFields: {sold: 'price', income: 'income', cut: 'takersCut', revenue: 'revenue'},
  },
  Currency: {
    subRequest: 10054,
    key: 'currencySellOrder',
    targetKey: 'currencySellOrders',
    filterKey: 'currencySellOrder',
    responseKey: 'currencySellOrders',
    changesKey: 'currencySellOrder',
    addSubRequest: 10056,
    updateSubRequest: 10057,
    removeSubRequest: 10058,
    detailsSubRequest: 10055,
    summaryFields: {sold: 'price', income: 'income', cut: 'takersCut', revenue: 'revenue'},
  },
  CurrencyBuy: {
    subRequest: 10045,
    key: 'currencyBuyOrder',
    targetKey: 'currencyBuyOrders',
    filterKey: 'currencyBuyOrder',
    responseKey: 'currencyBuyOrders',
    changesKey: 'currencyBuyOrder',
    addSubRequest: 10047,
    updateSubRequest: 10048,
    removeSubRequest: 10049,
    detailsSubRequest: 10046,
    summaryFields: {amount: 'amount', value: 'value', tomanValue: 'tomanValue'},
  },
  'Gift Card': {
    subRequest: 10147,
    key: 'giftCardOrder',
    targetKey: 'giftCardOrders',
    filterKey: 'giftCardOrder',
    responseKey: 'giftCardOrders',
    changesKey: 'giftCardOrder',
    addSubRequest: 10149,
    updateSubRequest: 10150,
    removeSubRequest: 10151,
    detailsSubRequest: 10148,
    summaryFields: {sold: 'price', income: 'income', cut: 'takersCut', revenue: 'revenue'},
  },
  Item: {
    subRequest: 10142,
    key: 'itemOrder',
    targetKey: 'itemOrders',
    filterKey: 'itemOrder',
    responseKey: 'itemOrders',
    changesKey: 'itemOrder',
    addSubRequest: 10144,
    updateSubRequest: 10145,
    removeSubRequest: 10146,
    detailsSubRequest: 10143,
    summaryFields: {sold: 'price', income: 'income', cut: 'takersCut', revenue: 'revenue'},
  },
  Shop: {
    subRequest: 10137,
    key: 'shopOrder',
    targetKey: 'shopOrders',
    filterKey: 'shopOrder',
    responseKey: 'shopOrders',
    changesKey: 'shopOrder',
    addSubRequest: 10139,
    updateSubRequest: 10140,
    removeSubRequest: 10141,
    detailsSubRequest: 10138,
    summaryFields: {
      price: 'price',
      tomanPrice: 'tomanPrice',
      cost: 'cost',
      tomanCost: 'tomanCost',
      revenue: 'revenue',
      tomanRevenue: 'tomanRevenue',
      usd_tmn: 'usd_tmn',
    },
  },
  currencyTransfer: {
    subRequest: 10069,
    key: 'currencyTransfer',
    targetKey: 'stockOrders',
    changesKey: 'stockOrder',
    addSubRequest: 10071,
    updateSubRequest: 10072,
    removeSubRequest: 10073,
    detailsSubRequest: 10070,
  },
}

export const useMainOrdersStore = defineStore('mainOrders', {
  state: () => ({
    boostOrders: [],
    boostFilteredOrders: [],
    boostOrdersSort: null,
    boostOrdersSearch: null,
    selectedBoostOrders: [],
    /*====================*/
    currencyBuyOrders: [],
    currencyBuyFilteredOrders: [],
    currencyBuyOrdersSort: null,
    currencyBuyOrdersSearch: null,
    selectedCurrencyBuyOrders: [],
    /*====================*/
    currencySellOrders: [],
    currencySellFilteredOrders: [],
    currencySellOrdersSort: null,
    currencySellOrdersSearch: null,
    selectedCurrencySellOrders: [],
    /*====================*/
    accountOrders: [],
    accountFilteredOrders: [],
    accountOrdersSort: null,
    accountOrdersSearch: null,
    selectedAccountOrders: [],
    /*====================*/
    giftCardOrders: [],
    giftCardFilteredOrders: [],
    giftCardOrdersSort: null,
    giftCardOrdersSearch: null,
    selectedGiftCardOrders: [],
    /*====================*/
    itemOrders: [],
    itemFilteredOrders: [],
    itemOrdersSort: null,
    itemOrdersSearch: null,
    selectedItemOrders: [],
    /*====================*/
    shopOrders: [],
    shopFilteredOrders: [],
    shopOrdersSort: null,
    shopOrdersSearch: null,
    selectedShopOrders: [],
    /*====================*/
    stockOrders: [],
    stocks: [],
    /*====================*/
    gameAccount: null,
    gameAccounts: [],
    fullGameAccounts: [],
    gameAccountsSearch: null,
    /*====================*/
    stockGolds: [],
    stockSearch: null,
    /*====================*/
    filters: {
      'finalized': false,
      'stores': [],
      'games': [],
      'states': [],
      'createdFrom': null,
      'createdTo': null,
      'updatedFrom': null,
      'updatedTo': null,
      'lastUpdate': null
    },
    shopStateFilter: [],
    paymentMethodsFilter: [],
    availableStates: [
      {
        value: 'New',
        label: 'New'
      },
      {
        value: 'Delivering',
        label: 'Delivering'
      },
      {
        value: 'Delivered',
        label: 'Delivered'
      },
      {
        value: 'Canceled',
        label: 'Canceled'
      },
      {
        value: 'Paid',
        label: 'Paid'
      },
    ],
    availableShopStates: [
      {
        value: 'New',
        label: 'New'
      },
      {
        value: 'Delivering',
        label: 'Delivering'
      },
      {
        value: 'Delivered',
        label: 'Delivered'
      },
      {
        value: 'Completed',
        label: 'Completed'
      },
      {
        value: 'Canceled',
        label: 'Canceled'
      },
      {
        value: 'Settled',
        label: 'Settled'
      },
    ],
    isLoading: false,
    isShowFilterModal: false,
    isShowGamesModal: false,
    isShowStoresModal: false,
    isShowPaymentMethodsModal: false,
    isShowStatesModal: false,
    isShowShopStatesModal: false,
    isGettingNewOrders: false,
    currencyTransferOrdersSort: null,
    currencyTransferOrdersSearch: null,
  }),
  getters: {
    getOrders: state => type => state[`${type}Orders`],
    limitedOrders: (state) => {
      return (type, limit) => {
        const list = state[`${type}Orders`] || []
        return limit ? list.slice(0, limit) : list
      }
    },
    getSummary: (state) => (type, orderType, summaryType) => {
      let ordersToSummary;
      if (state[`selected${summaryType}Orders`].length > 0) {
        ordersToSummary = state[`selected${summaryType}Orders`].filter(sel =>
          state[`${type}FilteredOrders`].some(order => order.id === sel.id)
        );
      } else {
        ordersToSummary = state[`${type}FilteredOrders`]
      }
      return summarizeOrders(ordersToSummary, ORDER_TYPES[orderType].summaryFields)
    },
    shopSummary(state) {
      let orders;
      if (state[`selectedShopOrders`].length > 0) {
        orders = state[`selectedShopOrders`].filter(sel =>
          state.shopFilteredOrders.some(order => order.id === sel.id)
        );
      } else {
        orders = state.shopFilteredOrders
      }
      const summary = {
        price: 0,
        tomanPrice: 0,
        cost: 0,
        tomanCost: 0,
        revenue: 0,
        tomanRevenue: 0,
        usd_tmn: 0,
        records: orders.length
      }

      orders.forEach(order => {
        summary.price += parseFloat(order.price || "0")
        summary.tomanPrice += parseFloat(order.tomanPrice || "0")
        summary.cost += parseFloat(order.cost || "0")
        summary.tomanCost += parseFloat(order.tomanCost || "0")
        summary.revenue += parseFloat(order.revenue || "0")
        summary.tomanRevenue += parseFloat(order.tomanRevenue || "0")
        summary.usd_tmn += parseFloat(order.usd_tmn || "0")
      })

      // Calculate USD/TMN average:
      const usdTmnAverage = summary.records > 0 ? summary.usd_tmn / summary.records : 0

      // Prepare final summary as strings with 2 decimal places where applicable
      return {
        price: summary.price.toFixed(2),
        tomanPrice: (summary.tomanPrice.toFixed(0)) * 1000,
        cost: summary.cost.toFixed(2),
        tomanCost: (summary.tomanCost.toFixed(0)) * 1000,
        revenue: summary.revenue.toFixed(2),
        tomanRevenue: (summary.tomanRevenue.toFixed(0)) * 1000,
        usd_tmn: usdTmnAverage.toFixed(0),
        records: summary.records.toString()
      }
    },
    minimizedFilters(state) {
      return Object.fromEntries(
        Object.entries(state.filters).filter(([key, value]) => {
          if (Array.isArray(value)) return value.length > 0;
          return value !== null;
        })
      )
    },
    isFilterActive(state) {
      const f = state.filters;
      return (
        (f.stores && f.stores.length > 0) ||
        (f.games && f.games.length > 0) ||
        (f.states && f.states.length > 0) ||
        (state.shopStateFilter && state.shopStateFilter.length > 0) ||
        (state.paymentMethodsFilter && state.paymentMethodsFilter.length > 0) ||
        f.createdFrom !== null ||
        f.createdTo !== null ||
        f.updatedFrom !== null ||
        f.updatedTo !== null
      );
    },
    activeFilterCount(state) {
      const f = state.filters;
      let count = 0;

      if (f.stores.length > 0) count++;
      if (f.games.length > 0) count++;
      if (f.states.length > 0) count++;
      if (state.shopStateFilter.length > 0) count++;
      if (state.paymentMethodsFilter.length > 0) count++;
      if (f.createdFrom !== null) count++;
      if (f.createdTo !== null) count++;
      if (f.updatedFrom !== null) count++;
      if (f.updatedTo !== null) count++;

      return count;
    },
    getGameAccounts(state) {
      return state.gameAccounts
    },
    getGameAccountByGame: (state) => (gameName) => {
      return state.gameAccounts.filter((obj) => obj.game === gameName);
    },
    getStocks(state) {
      return state.stockGolds
    }
  },
  actions: {
    reset_filters() {
      this.shopStateFilter = [];
      this.paymentMethodsFilter = [];
      this.filters = {
        'finalized': this.filters.finalized,
        'stores': [],
        'games': [],
        'states': [],
        'createdFrom': null,
        'createdTo': null,
        'updatedFrom': null,
        'updatedTo': null,
        'lastUpdate': null
      };
    },
    reset_finalized() {
      this.filters.finalized = false;
    },
    reset_lastUpdate() {
      this.filters.lastUpdate = null;
    },
    reset_orders(targetKey) {
      this[targetKey] = [];
    },
    reset_selected_orders(orderType) {
      this[`selected${orderType}Orders`] = [];
    },
    show_filter_modal() {
      this.isShowFilterModal = true;
    },
    close_filter_modal() {
      this.isShowFilterModal = false;
    },
    show_stores_modal() {
      this.isShowStoresModal = true;
    },
    close_stores_modal() {
      this.isShowStoresModal = false;
    },
    show_states_modal() {
      this.isShowStatesModal = true;
    },
    close_states_modal() {
      this.isShowStatesModal = false;
    },
    show_paymentMethods_modal() {
      this.isShowPaymentMethodsModal = true;
    },
    close_paymentMethods_modal() {
      this.isShowPaymentMethodsModal = false;
    },
    show_shop_states_modal() {
      this.isShowShopStatesModal = true;
    },
    close_shop_states_modal() {
      this.isShowShopStatesModal = false;
    },
    show_games_modal() {
      this.isShowGamesModal = true;
    },
    close_games_modal() {
      this.isShowGamesModal = false;
    },
    set_filter(key, value) {
      this.filters[key] = Array.isArray(value) ? value.map(item => item.value) : value;
      //console.log(this.filters)
    },
    setGameAccounts(payload) {
      this.gameAccounts = payload
    },
    setFullGameAccounts(payload) {
      this.fullGameAccounts = payload
    },
    setGameAccount(payload) {
      this.gameAccount = payload
    },
    /*====================*/
    setBoostOrdersSort(field, type) {
      this.boostOrdersSort = {
        field,
        type
      }
    },
    resetBoostOrdersSort() {
      this.boostOrdersSort = null
    },
    resetBoostOrdersSearch() {
      this.boostOrdersSearch = null
    },
    /*====================*/
    setCurrencyBuyOrdersSort(field, type) {
      this.currencyBuyOrdersSort = {
        field,
        type
      }
    },
    resetCurrencyBuyOrdersSort() {
      this.currencyBuyOrdersSort = null
    },
    resetCurrencyBuyOrdersSearch() {
      this.currencyBuyOrdersSearch = null
    },
    /*====================*/
    setCurrencySellOrdersSort(field, type) {
      this.currencySellOrdersSort = {
        field,
        type
      }
    },
    resetCurrencySellOrdersSort() {
      this.currencySellOrdersSort = null
    },
    resetCurrencySellOrdersSearch() {
      this.currencySellOrdersSearch = null
    },
    /*====================*/
    setAccountOrdersSort(field, type) {
      this.accountOrdersSort = {
        field,
        type
      }
    },
    resetAccountOrdersSort() {
      this.accountOrdersSort = null
    },
    resetAccountOrdersSearch() {
      this.accountOrdersSearch = null
    },
    /*====================*/
    setGiftCardOrdersSort(field, type) {
      this.giftCardOrdersSort = {
        field,
        type
      }
    },
    resetGiftCardOrdersSort() {
      this.giftCardOrdersSort = null
    },
    resetGiftCardOrdersSearch() {
      this.giftCardOrdersSearch = null
    },
    /*====================*/
    setItemOrdersSort(field, type) {
      this.itemOrdersSort = {
        field,
        type
      }
    },
    resetItemOrdersSort() {
      this.itemOrdersSort = null
    },
    resetItemOrdersSearch() {
      this.itemOrdersSearch = null
    },
    /*====================*/
    setShopOrdersSort(field, type) {
      this.shopOrdersSort = {
        field,
        type
      }
    },
    resetShopOrdersSort() {
      this.shopOrdersSort = null
    },
    resetShopOrdersSearch() {
      this.shopOrdersSearch = null
    },
    /*====================*/
    setCurrencyTransferOrdersSort(field, type) {
      this.currencyTransferOrdersSort = {
        field,
        type
      }
    },
    resetCurrencyTransferOrdersSort() {
      this.currencyTransferOrdersSort = null
    },
    resetCurrencyTransferOrdersSearch() {
      this.currencyTransferOrdersSearch = null
    },
    async action_getGameAccounts() {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10068
      };
      //console.log('action_getGameAccounts: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_getGameAccounts Success: ', response);
          this.setGameAccounts(response.data.gameAccounts)
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return false;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return false;
        }
      );
    },
    async action_getFullGameAccounts() {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10063
      };
      //console.log('action_getFullGameAccounts: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_getFullGameAccounts Success: ', response);
          this.setFullGameAccounts(response.data.gameAccounts)
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return false;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return false;
        }
      );
    },
    async action_getGameAccount(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10064,
        "gameAccount": {
          "id": payload.id
        }
      };
      this.setGameAccount(null)
      //console.log('action_getGameAccount: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_getGameAccount Success: ', response);
          this.setGameAccount(response.data.gameAccount)
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return false;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return false;
        }
      );
    },
    async action_removeGameAccount(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10067,
        "gameAccount": {
          "id": payload.id
        }
      };
      //console.log('action_removeGameAccount: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_removeGameAccount Success: ', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addGameAccount(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10065,
        "gameAccount": {
          "game": payload.game,
          "title": payload.title,
          "username": payload.username,
          "password": payload.password,
          "isActive": payload.isActive
        }
      };
      //console.log('action_addGameAccount: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_addGameAccount Success: ', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateGameAccount(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10066,
        "gameAccount": {
          "id": payload.id,
          "game": payload.game,
          "title": payload.title,
          "username": payload.username,
          "password": payload.password,
          "isActive": payload.isActive
        }
      };
      //console.log('action_updateGameAccount: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_updateGameAccount Success: ', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async fetchAndMergeOrders({subRequest, filterKey, responseKey, targetKey, changesKey}) {
      const userStore = useUserStore();
      const request = {
        ...getAuthenticatedRequest(),
        subRequest,
        [filterKey]: this.minimizedFilters
      };

      //console.log(`Fetching ${targetKey} with subRequest ${subRequest}`, request);

      if (this[targetKey].length === 0) {
        this.isLoading = true
      }

      if (this.isGettingNewOrders === false) {
        this.isGettingNewOrders = true;

        return await api.post("", request)
          .then((res) => (res.status === 200 ? res.data : console.log("Response status:", res.status)))
          .then((data) => {
            //if (data[responseKey].length > 0) console.log(data[responseKey])
            if (data?.response === 100) {
              const newOrders = data[responseKey] || [];

              // اولین بار
              if (this[targetKey].length === 0) {
                this[targetKey] = newOrders;
              } else {
                newOrders.forEach(newOrder => {
                  const index = this[targetKey].findIndex(
                    o => o.uuid === newOrder.uuid || o.id === newOrder.id
                  );
                  if (index !== -1) {
                    this[targetKey][index] = newOrder;
                  } else {
                    this[targetKey].unshift(newOrder)
                  }
                });
              }

              /*console.log(this.minimizedFilters)*/
              this[targetKey] = this[targetKey].filter(order => {
                const statesFilter = Array.isArray(this.minimizedFilters?.states)
                  ? this.minimizedFilters.states
                  : []
                const gamesFilter = Array.isArray(this.minimizedFilters?.games)
                  ? this.minimizedFilters.games
                  : []
                const storesFilter = Array.isArray(this.minimizedFilters?.stores)
                  ? this.minimizedFilters.stores
                  : []

                // finalized filter
                const finalizedFilter = typeof this.minimizedFilters?.finalized === "boolean"
                  ? this.minimizedFilters.finalized
                  : null

                const stateMatch = statesFilter.length === 0 ? true
                  : statesFilter.map(s => s.toLowerCase()).includes(order.state.toLowerCase())
                const gameMatch = gamesFilter.length === 0 ? true
                  : gamesFilter.map(s => s.toLowerCase()).includes(order.game.toLowerCase())
                const storeMatch = storesFilter.length === 0 ? true
                  : storesFilter.map(s => s.toLowerCase()).includes(order.store.toLowerCase())

                const creationMatch =
                  (this.minimizedFilters.createdFrom == null || order.creationTime >= this.minimizedFilters.createdFrom) &&
                  (this.minimizedFilters.createdTo == null || order.creationTime <= this.minimizedFilters.createdTo)

                const updateMatch =
                  (this.minimizedFilters.updatedFrom == null || order.lastUpdate >= this.minimizedFilters.updatedFrom) &&
                  (this.minimizedFilters.updatedTo == null || order.lastUpdate <= this.minimizedFilters.updatedTo)

                const finalizedMatch = finalizedFilter === null ? true : order.finalized === finalizedFilter

                return stateMatch && gameMatch && storeMatch && creationMatch && updateMatch && finalizedMatch
              })

              // lastUpdate
              const latest = data[changesKey]?.lastUpdate

              if (latest) {
                this.filters.lastUpdate = latest;
              }

            } else if (data?.response === -100002) {
              userStore.resetUser("Your credential is expired");
              router.push({name: "Login"});
            } else {
              this.errorMessage = data?.message || "Unknown error";
            }
          })
          .catch((err) => {
            console.error("API error:", err);
          })
          .finally(() => {
            this.isLoading = false;
            this.isGettingNewOrders = false;
          });
      }
    },
    async action_get_order(type, orderID) {
      const userStore = useUserStore();
      const isNumericId = /^\d+$/.test(orderID);

      // Get the corresponding order type configuration
      const selectedOrder = ORDER_TYPES[type];

      const request = selectedOrder
        ? {
          ...getAuthenticatedRequest(),
          subRequest: selectedOrder.detailsSubRequest,
          [selectedOrder.key]: isNumericId ? {id: orderID} : {uuid: orderID}
        }
        : null; // Handle unexpected cases (e.g., type not found)

      return await api.post("", request)
        .then((res) => (res.status === 200 ? res.data : console.log("Response status:", res.status)))
        .then((data) => {
          if (data?.response === 100) {
            if(!data[selectedOrder.key]) return false
            return data[selectedOrder.key]
          } else if (data?.response === -100002) {
            userStore.resetUser("Your credential is expired");
            router.push({name: "Login"});
          } else {
            this.errorMessage = data?.message || "Unknown error";
          }
        })
        .catch((err) => {
          console.error("API error:", err);
        })
        .finally(() => {
          //console.log('true')
        });
    },
    async updateOrder(type, payload) {
      const config = ORDER_TYPES[type];
      if (!config) return;

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.updateSubRequest,
        [config.key]: {...payload},
      };

      //console.log(`action_update${config.key}: `, request);
      return await useApi(
        request,
        (response) => {
          //console.log(`update ${config.key} Success: `, response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async addOrder(type, payload) {
      const config = ORDER_TYPES[type];
      if (!config) return;

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.addSubRequest,
        [config.key]: {...payload},
      };
      //console.log(`action_add${config.key}: `, request);
      return await useApi(
        request,
        (response) => {
          //console.log(`add ${config.key} Success: `, response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async addOrder2(type, payload) {
      const config = ORDER_TYPES[type];
      if (!config) return;

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.addSubRequest,
        [config.key]: {...payload},
      };
      //console.log(`action_add${config.key}: `, request);
      return await useApi(
        request,
        (response) => {
          //console.log(`add ${config.key} Success: `, response);
          return {
            result: true,
            orderID: response.data[config.key].id
          };
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return {
            result: false,
            error: errorMessage
          }
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return {
            result: false,
            error: errorMessage
          }
        }
      );
    },
    async addCurrencyOrder(type, payload) {
      const config = ORDER_TYPES[type];
      if (!config) return;

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.addSubRequest,
        [config.key]: {...payload},
      };
      //console.log(`action_add${config.key}: `, request);
      return await useApi(
        request,
        (response) => {
          //console.log(`add ${config.key} Success: `, response);
          return {id: response.data[config.key].id, uuid: response.data[config.key].uuid};
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async removeOrder(type, payload) {
      const config = ORDER_TYPES[type];
      if (!config) return;

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.removeSubRequest,
        [config.key]: {id: payload.id},
      };
      //console.log(`action_remove${config.key}: `, request);
      return await useApi(
        request,
        (response) => {
          //console.log(`remove ${config.key} Success: `, response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getOrders(type) {
      const config = ORDER_TYPES[type];
      if (!config) {
        console.error(`Invalid order type: ${type}`);
        return;
      }
      return this.fetchAndMergeOrders(config);
    },

    async action_getOverviewStocks() {
      const request = {
        ...getAuthenticatedRequest(),
        subRequest: 10027
      }

      //console.log('action_getOverviewStocks: ', request);
      this.isLoading = true
      return await useApi(
        request,
        (response) => {
          //console.log('action_getOverviewStocks Success: ', response);
          this.stocks = response.data.stocks
          this.isLoading = false
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          this.isLoading = false
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          this.isLoading = false
          return errorMessage;
        }
      );

    },
    async action_getStocksOrders() {
      const request = {
        ...getAuthenticatedRequest(),
        subRequest: 10069
      }

      const currencyTransfer = {}

      if (this.filters.createdFrom) {
        currencyTransfer.startingTimestamp = this.filters.createdFrom
      }
      if (this.filters.createdTo) {
        currencyTransfer.endingTimestamp = this.filters.createdTo
      }

      if (Object.keys(currencyTransfer).length > 0) {
        request.currencyTransfer = currencyTransfer
      }
      //console.log('action_getStocksOrders: ', request);
      this.isLoading = true
      return await useApi(
        request,
        (response) => {
          //console.log('action_getStocksOrders Success: ', response);
          this.stockOrders = response.data.currencyTransfers
          this.isLoading = false
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          this.isLoading = false
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          this.isLoading = false
          return errorMessage;
        }
      );

    },
    async action_updateCurrencyTransferOrder(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10072,
        'currencyTransfer': {
          ...payload
        }
      };
      //console.log('action_updateCurrencyTransferOrder: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_updateCurrencyTransferOrder Success: ', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addCurrencyTransferOrder(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10071,
        'currencyTransfer': {
          ...payload
        }
      };
      //console.log('action_addCurrencyTransferOrder: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_addCurrencyTransferOrder Success: ', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getAggregatedGoldStocks() {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2033
      };
      //console.log('action_getAllGoldStock', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getAllGoldStock Success', response);
          this.stockGolds = response.data.stocks
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getAllGoldStocks(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2037,
        "stock": {
          "game": payload.game
        }
      };
      //console.log('action_getAllGoldStocks', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getAllGoldStocks Success', response);
          return response.data.stocks
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getGoldStock() {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2034
      };
      //console.log('action_getGoldStock', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getGoldStock Success', response);
          this.stockGolds = response.data.stocks
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addGoldStock(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2035,
        ...payload
      };
      //console.log('action_addGoldStock', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addGoldStock Success', response);
          return true
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeGoldStock(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2036,
        ...payload
      };
      //console.log('action_removeGoldStock', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeGoldStock Success', response);
          return true
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders_currencySell(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10102,
        'currencySellOrder': {...payload}
      };
      //console.log('action_getHotOrders_currencySell', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrders_currencySell Success', response);
          return response.data.currencySellOrders;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder_currencySell(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10103,
        'currencySellOrder': {...payload}
      };
      //console.log('action_getHotOrder_currencySell', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrder_currencySell Success', response);
          return response.data.currencySellOrder;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder_currencySell(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10104,
        'currencySellOrder': {...payload}
      };
      //console.log('action_addHotOrder_currencySell', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addHotOrder_currencySell Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder_currencySell(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10105,
        'currencySellOrder': {...payload}
      };
      //console.log('action_updateHotOrder_currencySell', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_updateHotOrder_currencySell Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeHotOrder_currencySell(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10106,
        'currencySellOrder': {...payload}
      };
      //console.log('action_removeHotOrder_currencySell', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeHotOrder_currencySell Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders_item(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2020,
        'itemOrder': {...payload}
      };
      //console.log('action_getHotOrders_item', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrders_item Success', response);
          return response.data.itemOrders;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder_item(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2021,
        'itemOrder': {...payload}
      };
      //console.log('action_getHotOrder_item', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrder_item Success', response);
          return response.data.itemOrder;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder_item(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2022,
        'itemOrder': {...payload}
      };
      //console.log('action_addHotOrder_item', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addHotOrder_item Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder_item(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2023,
        'itemOrder': {...payload}
      };
      //console.log('action_updateHotOrder_item', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_updateHotOrder_item Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeHotOrder_item(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2024,
        'itemOrder': {...payload}
      };
      //console.log('action_removeHotOrder_item', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeHotOrder_item Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders_currencyBuy(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10097,
        'currencyBuyOrder': {...payload}
      };
      //console.log('action_getHotOrders_currencyBuy', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrders_currencyBuy Success', response);
          return response.data.currencyBuyOrders;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder_currencyBuy(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10098,
        'currencyBuyOrder': {...payload}
      };
      //console.log('action_getHotOrder_currencyBuy', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrder_currencyBuy Success', response);
          return response.data.currencyBuyOrder;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder_currencyBuy(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10099,
        'currencyBuyOrder': {...payload}
      };
      //console.log('action_addHotOrder_currencyBuy', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addHotOrder_currencyBuy Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder_currencyBuy(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10100,
        'currencyBuyOrder': {...payload}
      };
      //console.log('action_updateHotOrder_currencyBuy', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_updateHotOrder_currencyBuy Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeHotOrder_currencyBuy(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10101,
        'currencyBuyOrder': {...payload}
      };
      //console.log('action_removeHotOrder_currencyBuy', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeHotOrder_currencyBuy Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders_boost(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10092,
        'boostOrder': {...payload}
      };
      //console.log('action_getHotOrders_boost', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrders_boost Success', response);
          return response.data.boostOrders;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder_boost(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10093,
        'boostOrder': {...payload}
      };
      //console.log('action_getHotOrder_boost', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrder_boost Success', response);
          return response.data.boostOrder;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder_boost(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10094,
        'boostOrder': {...payload}
      };
      //console.log('action_addHotOrder_boost', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addHotOrder_boost Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder_boost(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10095,
        'boostOrder': {...payload}
      };
      //console.log('action_updateHotOrder_boost', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_updateHotOrder_boost Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeHotOrder_boost(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10096,
        'boostOrder': {...payload}
      };
      //console.log('action_removeHotOrder_boost', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeHotOrder_boost Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders_giftCard(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2008,
        'giftCardOrder': {...payload}
      };
      //console.log('action_getHotOrders_giftCard', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrders_giftCard Success', response);
          return response.data.giftCardOrders;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder_giftCard(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2009,
        'giftCardOrder': {...payload}
      };
      //console.log('action_getHotOrder_giftCard', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrder_giftCard Success', response);
          return response.data.giftCardOrder;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder_giftCard(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2010,
        'giftCardOrder': {...payload}
      };
      //console.log('action_addHotOrder_giftCard', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addHotOrder_giftCard Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder_giftCard(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2011,
        'giftCardOrder': {...payload}
      };
      //console.log('action_updateHotOrder_giftCard', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_updateHotOrder_giftCard Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeHotOrder_giftCard(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2012,
        'giftCardOrder': {...payload}
      };
      //console.log('action_removeHotOrder_giftCard', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeHotOrder_boost Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders_account(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10087,
        'accountOrder': {...payload}
      };
      //console.log('action_getHotOrders_account', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrders_account Success', response);
          return response.data.accountOrders;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder_account(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10088,
        'accountOrder': {...payload}
      };
      //console.log('action_getHotOrder_account', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_getHotOrder_account Success', response);
          return response.data.accountOrder;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder_account(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10089,
        'accountOrder': {...payload}
      };
      //console.log('action_addHotOrder_account', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_addHotOrder_account Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder_account(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10090,
        'accountOrder': {...payload}
      };
      //console.log('action_updateHotOrder_account', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_updateHotOrder_account Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_removeHotOrder_account(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 10091,
        'accountOrder': {...payload}
      };
      //console.log('action_removeHotOrder_account', request);

      return await useApi(
        request,
        (response) => {
          //console.log('action_removeHotOrder_account Success', response);
          return true;
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getHotOrders(type, payload) {
      const configs = {
        CurrencySell: {
          code: 10102,
          key: 'currencySellOrder',
          responseKey: 'currencySellOrders',
        },
        Item: {
          code: 2020,
          key: 'itemOrder',
          responseKey: 'itemOrders',
        },
        CurrencyBuy: {
          code: 10097,
          key: 'currencyBuyOrder',
          responseKey: 'currencyBuyOrders',
        },
        Boost: {
          code: 10092,
          key: 'boostOrder',
          responseKey: 'boostOrders',
        },
        'Gift Card': {
          code: 2008,
          key: 'giftCardOrder',
          responseKey: 'giftCardOrders',
        },
        Account: {
          code: 10087,
          key: 'accountOrder',
          responseKey: 'accountOrders',
        },
      };

      const config = configs[type];
      if (!config) {
        throw new Error(`❌ Unknown hot order type: ${type}`);
      }

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.code,
        [config.key]: {...payload},
      };

      //console.log(`action_getHotOrders_${type}`, request);

      return await useApi(
        request,
        (response) => {
          //console.log(`✅ action_getHotOrders_${type} Success`, response);
          return {
            result: true,
            data: response.data[config.responseKey]
          };
        },
        (errorMessage) => {
          //console.log('⚠️ Handle Credential Expired:', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('❌ Handle Failure:', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getHotOrder(type, payload) {
      const configs = {
        CurrencySell: {code: 10103, key: 'currencySellOrder'},
        Item: {code: 2021, key: 'itemOrder'},
        CurrencyBuy: {code: 10098, key: 'currencyBuyOrder'},
        Boost: {code: 10093, key: 'boostOrder'},
        'Gift Card': {code: 2009, key: 'giftCardOrder'},
        Account: {code: 10088, key: 'accountOrder'},
      };

      const config = configs[type];
      if (!config) {
        throw new Error(`❌ Unknown hot order type: ${type}`);
      }

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.code,
        [config.key]: {...payload},
      };

      //console.log(`action_getHotOrder_${type}`, request);

      return await useApi(
        request,
        (response) => {
          //console.log(`✅ action_getHotOrder_${type} Success`, response);
          return {
            result: true,
            data: response.data[config.key]
          };
        },
        (errorMessage) => {
          //console.log('⚠️ Handle Credential Expired:', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('❌ Handle Failure:', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_addHotOrder(type, payload) {
      const subRequests = {
        CurrencySell: {code: 10104, key: 'currencySellOrder'},
        Item: {code: 2022, key: 'itemOrder'},
        CurrencyBuy: {code: 10099, key: 'currencyBuyOrder'},
        Boost: {code: 10094, key: 'boostOrder'},
        'Gift Card': {code: 2010, key: 'giftCardOrder'},
        Account: {code: 10089, key: 'accountOrder'},
      };

      const config = subRequests[type];
      if (!config) {
        throw new Error(`❌ Unknown hot order type: ${type}`);
      }

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.code,
        [config.key]: {...payload},
      };

      //console.log(`action_addHotOrder_${type}`, request);

      return await useApi(
        request,
        (response) => {
          //console.log(`✅ action_addHotOrder_${type} Success`, response);
          return {
            data: response.data[config.key],
            result: true
          };
        },
        (errorMessage) => {
          //console.log('⚠️ Handle Credential Expired:', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('❌ Handle Failure:', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_updateHotOrder(type, payload) {
      const subRequests = {
        CurrencySell: {code: 10105, key: 'currencySellOrder'},
        Item: {code: 2023, key: 'itemOrder'},
        CurrencyBuy: {code: 10100, key: 'currencyBuyOrder'},
        Boost: {code: 10095, key: 'boostOrder'},
        'Gift Card': {code: 2011, key: 'giftCardOrder'},
        Account: {code: 10090, key: 'accountOrder'},
      };

      const config = subRequests[type];
      if (!config) {
        throw new Error(`❌ Unknown hot order type: ${type}`);
      }

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.code,
        [config.key]: {...payload},
      };

      //console.log(`action_updateHotOrder_${type}`, request);

      return await useApi(
        request,
        (response) => {
          //console.log(`✅ action_updateHotOrder_${type} Success`, response);
          return {
            result: true
          };
        },
        (errorMessage) => {
          //console.log('⚠️ Handle Credential Expired:', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('❌ Handle Failure:', errorMessage);
          return errorMessage || 'Handle Failure';
        }
      );
    },
    async action_removeHotOrder(type, payload) {
      const subRequests = {
        CurrencySell: {code: 10106, key: 'currencySellOrder'},
        Item: {code: 2024, key: 'itemOrder'},
        CurrencyBuy: {code: 10101, key: 'currencyBuyOrder'},
        Boost: {code: 10096, key: 'boostOrder'},
        'Gift Card': {code: 2012, key: 'giftCardOrder'},
        Account: {code: 10091, key: 'accountOrder'},
      };

      const config = subRequests[type];
      if (!config) {
        throw new Error(`❌ Unknown hot order type: ${type}`);
      }

      const request = {
        ...getAuthenticatedRequest(),
        subRequest: config.code,
        [config.key]: {...payload},
      };

      //console.log(`action_removeHotOrder_${type}`, request);

      return await useApi(
        request,
        (response) => {
          //console.log(`✅ action_removeHotOrder_${type} Success`, response);
          return {
            result: true
          };
        },
        (errorMessage) => {
          //console.log('⚠️ Handle Credential Expired:', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('❌ Handle Failure:', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getTags() {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2050
      };

      return await useApi(
        request,
        (response) => {
          //console.log('action_getTags Success', response);
          return {
            result: true,
            data: response.data.tags
          };
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_setRatingAndTags(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2056,
        "order": payload
      };

      //console.log("action_setRatingAndTags Request", request)
      return await useApi(
        request,
        (response) => {
          //console.log('action_setRatingAndTags Success', response);
          return {
            result: true,
          };
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage;
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_getUserRatingAndTags(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2074,
        "profile": {
          "id": payload.id
        }
      };

      //console.log("action_getUserRatingAndTags Request", request)
      return await useApi(
        request,
        (response) => {
          //console.log('action_getUserRatingAndTags Success', response);
          return {
            result: true,
            data: response.data.profile.ratedOrders
          };
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage || 'Handle Failure';
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },

    async action_getDiscordChannels(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2078,
        "order": {
          "type": payload.orderType
        }
      };

      return await useApi(
        request,
        (response) => {
          //console.log('action_getDiscordChannels Success', response.data.order.discordChannels);
          return {
            result: true,
            data: response.data.order.discordChannels
          };
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage || 'Handle Failure';
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    },
    async action_postDiscordAd(payload) {
      const request = {
        ...getAuthenticatedRequest(),
        'subRequest': 2069,
        "order": {
          "id": payload.id,
          "type": payload.orderType,
          "discordChannel": payload.discordChannel,
          "hidePrice": payload.hidePrice,
          "postAction": payload.postAction,
        }
      };
      //console.log('action_postDiscordAd Request: ', request);
      return await useApi(
        request,
        (response) => {
          //console.log('action_postDiscordAd Success: ', response.data);
          return {
            result: true
          };
        },
        (errorMessage) => {
          //console.log('Handle Credential Expired: ', errorMessage);
          return errorMessage || 'Handle Failure';
        },
        (errorMessage) => {
          //console.log('Handle Failure: ', errorMessage);
          return errorMessage;
        }
      );
    }
  }
})